import { MouseEvent, ChangeEvent, FormEvent, useState } from "react";

import {
  useInput,
  useArray,
  useMultipleDropdown,
  useApisManagersBusinessDivisionsIndex,
} from "~/hooks";

import {
  BusinessDivisionType,
  OptionType,
  ManagersBusinessDivisionsIndexRequestType,
  MultiValueType,
} from "~/domains";

type ReturnType = {
  businessDivisions: BusinessDivisionType[];
  hasNextPage?: boolean;
  keyword: string;
  isLoading: boolean;
  selectableDropdownBusinessDivisions: OptionType[];
  selectedDropdownBusinessDivisions: MultiValueType<OptionType>;
  onChangeDropdownBusinessDivisions: (
    value: MultiValueType<OptionType>,
  ) => void;
  deleteIndexItem: (index: number) => void;
  findAndRemove: (func: (item: BusinessDivisionType) => boolean) => void;
  unshiftItem: (item: BusinessDivisionType) => void;
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  fetchNextPage: () => void;
  onConditionReset: (e: MouseEvent<HTMLButtonElement>) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
};

export const useSearchManagersBusinessDivisions = (): ReturnType => {
  const [{ value: keyword, onChange: onChangeKeyword }, setKeyword] =
    useInput("");

  const [
    {
      dropdownValue: selectedDropdownBusinessDivisions,
      onChange: onChangeDropdownBusinessDivisions,
      dropdownSelectableValue: selectableDropdownBusinessDivisions,
      setSelectableOption: setSelectableDropdownBusinessDivisions,
    },
  ] = useMultipleDropdown();

  const [params, setParams] =
    useState<ManagersBusinessDivisionsIndexRequestType>();
  const {
    items: businessDivisions,
    setItems: setBusinessDivisions,
    deleteIndexItem,
    unshiftItem,
    findAndRemove,
  } = useArray<BusinessDivisionType>([]);

  const { fetchNextPage, hasNextPage, isFetching } =
    useApisManagersBusinessDivisionsIndex({
      params,
      config: {
        onSuccess: (data) => {
          setSelectableDropdownBusinessDivisions(
            data.pages[0]?.selectableBusinessDivisions || [],
          );
          setBusinessDivisions(
            data.pages.map((page) => page.businessDivisions).flat(),
          );
        },
      },
    });

  const onSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setParams({
      keyword,
      businessDivisionIds: selectedDropdownBusinessDivisions.map(
        (division) => division.value,
      ),
    });
  };

  const onConditionReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setKeyword("");
    onChangeDropdownBusinessDivisions([]);
    setParams({});
  };

  return {
    businessDivisions,
    hasNextPage,
    isLoading: isFetching,
    selectableDropdownBusinessDivisions,
    selectedDropdownBusinessDivisions,
    keyword,
    fetchNextPage,
    onChangeDropdownBusinessDivisions,
    unshiftItem,
    deleteIndexItem,
    findAndRemove,
    onChangeKeyword,
    onConditionReset,
    onSearchSubmit,
  };
};
